import { CACHED_DATA } from './constants.js';

export const AGENT_CONFIG = [
/*
    {
        name: "Translate",
        desc: "Precision terminology translation",
        regex: /^(@translate)/i,
        getPrompt: (text) => {
            const source = JSON.parse(CACHED_DATA.glossary);
            let terms = {};
            Object.keys(source).forEach(k => { if (text.includes(k)) terms[k] = source[k]; });
            const glossary_data = JSON.stringify(terms);
            return `Role\nYou are the Terminology Enforcement Engine and a precise translator.\nYou are NOT a creative translator. You are a code-compliant text processor.\n\nData Injection\nThe following JSON data represents the ONLY valid translation for specific terms.\nYou must parse this JSON object and store it in your working memory as the "Strict Index".\n\n<<GLOSSARY_START>>\n${glossary_data}\n<<GLOSSARY_END>>\n\n# Input Text\nUser Source: "${text}"\n\n# Behavior Rules (MUST follow exactly)\n1. Extraction: Identify any terms in the User Source that match keys in the Strict Index.\n2. Locking: For every match, use the EXACT corresponding value from the Strict Index (do NOT modify these locked terms).\n   - CRITICAL: Output MUST be PLAIN TEXT. DO NOT use markdown, asterisks, or other formatting.\n3. Reconstruction: Translate the text to form a grammatical sentence around the locked terms.\n4. Final Audit: Ensure locked terms are unchanged and the output contains no markdown.\n\n# Output Format\nOutput ONLY the final plain-text translation. No bold, no highlights.`;
        }
    },
*/
    {
        name: "Chapter",
        desc: "Query content chapter and section",
        regex: /^(@chapter)/i,
        getPrompt: (text) => `ROLE AND OBJECTIVE
You are the Accounting Chapter Navigator, an assistant for the book Accounting 8th Edition. Your specific goal is to map user questions to the exact section in the provided context.

TOOLS 
You have access to the chapter structure: ${CACHED_DATA.chapter}

# INPUT TO CLASSIFY
User Question: "${text}"

STEP BY STEP INSTRUCTIONS
1. Analyze the "User Question" above.
2. Compare the core concept against the summaries and keywords in the TOOLS data. Look for semantic matches.
3. Select the most relevant section id and title.

RESPONSE FORMAT
Your response must contain exactly two parts without using markdown formatting symbols like hashes or asterisks. Ensure output is PLAIN TEXT.

RELEVANT SECTION
State the Section ID and Section Title clearly.

PART REASONING
Explain why you selected this section.`
    },
    {
        name: "Level",
        desc: "Analyze difficulty level",
        regex: /^(@level)/i,
        getPrompt: (text) => `You are an expert Accounting Education Agent designed to classify the difficulty of accounting questions. Your goal is to analyze the user question and assign a difficulty level based on the strict criteria below.

# Input
User Question: "${text}"

# Criteria
Easy: Tests definition, recall, or basic conceptual recognition, fully mechanical. Requires no calculations or only 2-3 routine calculation steps. Involves single standard accounting procedure.
Medium: Requires procedural application of accounting rules. Involves multi-step calculation or adjustment. Requires selecting and applying the correct method, but rules are explicit.
Hard: Requires integrating multiple concepts or standards. Demands judgment or interpretation rather than mechanical computation. Requires comparative reasoning.

# Output Formatting
Provide a PLAIN TEXT response. Do not use markdown, JSON, or emojis. Structure your response as follows:

Difficulty Level: [easy/medium/hard]

Analysis:
[Your reasoning in 2-3 concise sentences explaining why this level was assigned.]

Key Indicators:
- [Indicator 1]
- [Indicator 2]`
    },
    {
        name: "Solution",
        desc: "Retrieve official answer from database",
        regex: /^(@solution)/i,
        getPrompt: (question, dbAnswer) => {
            if (!dbAnswer) return `User asked: "${question}". System: No answer found in database. Please respond with the official disclaimer: "Official solution is currently unavailable for this question."`;
            return `Role: You are a Professional Instructor.
Data Source: The database returned this exact answer: "${dbAnswer}"
User Question: "${question}"

Task: Respond to the user strictly using the following layout without markdown bolding.
Question: [Extract the first 30 to 50 words of the User Question]
Answer: ${dbAnswer}
Explanation: [Explain the logic or concepts behind the Answer in simple, easy-to-understand language]`;
        }
    },
    {
        name: "NewQuestion",
        desc: "Generate a new variation of the selected question.",
        regex: /^(@newquestion)/i,
        format: "@newquestion <Original Question Text>"
    }
];

export const getDynamicNewQuestionPrompt = (selectedData) => {
    const aCat = String(selectedData.a_category || selectedData.A_Category || '').toLowerCase();
    const qCat = String(selectedData.q_category || selectedData.Q_Category || '').toLowerCase();

    // Derive options metadata dynamically — never hardcode option count or labels
    const optionsObj = selectedData.options || null;
    const isMCQ = qCat === 'mcq';
    const optionKeys = optionsObj ? Object.keys(optionsObj) : [];
    const optionCount = optionKeys.length;

    // ─── Base Module (options JSON injected for LLM context) ──────────────────
    let prompt = `Role: You are a professional instructional designer. Generate a new practice question based on the original data.\n\nOriginal Data:\nQuestion: ${selectedData.question || 'N/A'}\nOptions: ${JSON.stringify(optionsObj)}\nAnswer: ${selectedData.answer || 'N/A'}\n\n`;

    // ─── A_Category Logic Module (strict match) ────────────────────────────────
    if (aCat === 'calculate') {
        prompt += `Constraint (Logic - Calculate): Modify numerical parameters (amounts, dates, percentages) and entity names ONLY. The core accounting logic, formula structure, and difficulty level MUST remain identical to the original.\n`;
    } else if (aCat === 'definition') {
        prompt += `Constraint (Logic - Definition): [CRITICAL RULE] You are ONLY allowed to rephrase the question scenario or context. You MUST copy the original answer text EXACTLY into the "Explanation" field, word for word. DO NOT paraphrase, summarize, or alter any part of the original answer.\n`;
    }

    // ─── Q_Category Format Module (strict match, dynamic option count) ─────────
    if (isMCQ && optionCount > 0) {
        prompt += `Constraint (Format - Multiple Choice): Generate exactly ${optionCount} options labeled ${optionKeys.join(', ')}. STRICT RULE: All ${optionCount} option values MUST be textually and numerically UNIQUE — zero duplicates allowed. Ensure exactly one clearly correct answer.\n`;
    } else {
        prompt += `Constraint (Format - Open Ended): Generate an open-ended question with a comprehensive suggested answer. The "Options" field MUST be null.\n`;
    }

    // ─── Output Schema (fully dynamic — keys derived from original options) ────
    const schemaOptions = (isMCQ && optionCount > 0)
        ? JSON.stringify(
            Object.fromEntries(optionKeys.map(k => [k, `Unique option ${k} (different from all others)`])),
            null, 6)
        : 'null';

    prompt += `\nOutput Constraints:\nOutput ONLY RAW JSON. No conversational text. No \`\`\`json wrapper. Output must be directly parseable by JSON.parse().
Do NOT include any validation steps, self-check notes, chain-of-thought, reasoning traces, or internal verification in the JSON output.

EXACT JSON SCHEMA (do not deviate):
{
   "Question": "Generated question text here.",
   "Options": ${schemaOptions},
   "Answer_Key": "${(isMCQ && optionCount > 0) ? `Single correct letter: ${optionKeys.join(' or ')}` : 'Full answer text'}",
    "Explanation": "Concise learner-facing explanation of the answer. Keep it brief and exclude any hidden reasoning, validation, or self-check content."
}`;

    if (selectedData.variantDirective) {
        prompt += `\n\n[SYSTEM DIRECTIVE]: ${selectedData.variantDirective}`;
    }

    // Expose resolved matrix cell for caller Debugger tagging
    selectedData._resolvedACat = aCat || 'unknown';
    selectedData._resolvedQCat = qCat || 'unknown';

    return prompt;
};
